import borders from "assets/theme/base/borders";
import radialGradient from "assets/theme/functions/radialGradient";
import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";
import VuiSwitch from "components/VuiSwitch";
import VuiTypography from "components/VuiTypography";
import GradientBorder from "examples/GradientBorder";
import { IoArrowBack, IoCloseCircle, IoEye, IoEyeOff } from "react-icons/io5";
import { FcGoogle } from "react-icons/fc";
import { IconButton } from "@mui/material";
import palette from "assets/theme/base/colors";
import Link from "@mui/material/Link";
import { inputValidation } from "../helpers/input-validation.helper";
import { useState, useEffect, useRef } from "react";

export function SignInForm({
    pageData,
    form,
    handleLogin,
    handleGoogleSignIn,
    success,
    setSuccess,
}) {
    const [errors, setErrors] = useState({ email: false, password: false });
    const [errorMessage, setErrorMessage] = useState({ email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const googleButtonRef = useRef(null);

    const handleValidation = ({ input, value }) => {
        const { validations } = input;
        const message = inputValidation({ validations, value });
        setErrors({ ...errors, [input.id]: Boolean(message) });
        setErrorMessage({ ...errorMessage, [input.id]: message });
    };
    
    useEffect(() => {
        if (success && googleButtonRef.current && window.google) {
            try {
                window.google.accounts.id.renderButton(
                    googleButtonRef.current,
                    {
                        theme: 'filled_blue',
                        size: 'large',
                        text: 'continue_with',
                        width: googleButtonRef.current.offsetWidth
                    }
                );
            } catch (error) {
                console.error("Error renderizando botón de Google:", error);
            }
        }
    }, [success]);

    
    return (
        <VuiBox
            component="form"
            noValidate
            onSubmit={handleLogin}
            role="form"
            autoComplete="off"
            minWidth="450px"
            maxWidth="528px"
            width="100%"
            sx={({ breakpoints }) => ({
                border: "1px solid",
                borderRadius: "20px",
                padding: "90px",
                background: "linear-gradient(135deg, rgba(6,11,38,0.74) 0%, rgba(6,43,99,0.5) 100%);",
                [breakpoints.down("md")]: {
                    border: "none",
                    background: "none",
                    padding: "24px",
                    minWidth: "auto",
                },
            })}
        >
            {success ? (
                <>
                    <VuiTypography
                        component="h1"
                        color="white"
                        sx={{
                            fontSize: "30px",
                            fontWeight: "700",
                            lineHeight: "30px",
                            marginBottom: "20px",
                        }}
                    >
                        {form.title}
                    </VuiTypography>
                    {form.inputs.map((input) => (
                        <VuiBox mb={0} key={input.id}>
                            <VuiBox mb={0} ml={0.5}>
                                <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
                                    {input.label}
                                </VuiTypography>
                            </VuiBox>
                            <GradientBorder
                                minWidth="100%"
                                borderRadius={borders.borderRadius.lg}
                                padding="1px"
                                backgroundImage={radialGradient(
                                    palette.gradients.borderLight.main,
                                    palette.gradients.borderLight.state,
                                    palette.gradients.borderLight.angle
                                )}
                            >
                                <VuiBox sx={{ position: "relative" }}>
                                    <VuiInput
                                        type={input.type === "password" && showPassword ? "text" : input.type}
                                        placeholder={input.placeholder}
                                        required
                                        onChange={(e) => handleValidation({ input, value: e.target.value })}
                                        onBlur={(e) => handleValidation({ input, value: e.target.value })}
                                        id={input.id}
                                        sx={({ typography: { size }, breakpoints }) => ({
                                            fontSize: size.md,
                                            paddingRight: input.type === "password" ? "48px" : "12px",
                                            width: "100%",
                                            [breakpoints.down("md")]: {
                                                height: "48px",
                                            },
                                        })}
                                    />
                                    {input.type === "password" && (
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            sx={{
                                                position: "absolute",
                                                right: "4px",
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                padding: "6px",
                                                color: "rgba(255, 255, 255, 0.6)",
                                                zIndex: 10,
                                                "&:hover": {
                                                    color: "rgba(255, 255, 255, 0.9)",
                                                    backgroundColor: "transparent",
                                                },
                                            }}
                                        >
                                            {showPassword ? <IoEyeOff size="18px" /> : <IoEye size="18px" />}
                                        </IconButton>
                                    )}
                                </VuiBox>
                            </GradientBorder>
                            <VuiTypography
                                variant="caption"
                                sx={({ palette: { error } }) => ({
                                    marginLeft: "13px",
                                    marginTop: "4px",
                                    display: "block",
                                    color: error.main,
                                })}
                            >
                                {errorMessage[input.id] || " "}
                            </VuiTypography>
                        </VuiBox>
                    ))}
                    <VuiBox display="flex" justifyContent="space-between" alignItems="baseline">
                        <VuiBox mt={2}>
                        </VuiBox>
                        <VuiTypography
                            variant="caption"
                            color="light"
                            fontWeight="medium"
                            sx={{ cursor: "pointer", userSelect: "none", textDecoration: "underline" }}
                        >
                            <Link href={form.forgotPassword.link} target="_blank">
                                {form.forgotPassword.label}
                            </Link>
                        </VuiTypography>
                    </VuiBox>
                    <VuiBox mt={4} mb={1}>
                        <VuiButton
                            type="submit"
                            color="info"
                            fullWidth
                            disabled={errors.email || errors.password}
                            sx={{
                                fontSize: "14px",
                                fontWeight: "700",
                                lineHeight: "21px",
                            }}
                        >
                            {form.submitButton.text}
                        </VuiButton>
                    </VuiBox>
                    
                    {/* Separador con "o" - OCULTO TEMPORALMENTE */}
                    {/*<VuiBox 
                        display="flex" 
                        alignItems="center" 
                        my={3}
                    >
                        <VuiBox 
                            flex={1} 
                            height="1px" 
                            sx={{ 
                                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)" 
                            }} 
                        />
                        <VuiTypography 
                            color="white" 
                            fontWeight="medium" 
                            sx={{ mx: 2, fontSize: "14px" }}
                        >
                            o
                        </VuiTypography>
                        <VuiBox 
                            flex={1} 
                            height="1px" 
                            sx={{ 
                                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)" 
                            }} 
                        />
                    </VuiBox>

                    {/* Botón de Google - Renderizado por Google SDK  - OCULTO TEMPORALMENTE */}
                    {/* <VuiBox 
                        mb={1}
                        ref={googleButtonRef}
                        sx={{
                            '& > div': {
                                width: '100% !important'
                            }
                        }}
                    /> */}
                </>
            ) : (
                <VuiBox
                    maxWidth="270px"
                    margin="auto"
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <VuiBox>
                        <IoCloseCircle size="41px" color="white" display="block" margin="auto" />
                    </VuiBox>
                    <VuiTypography
                        component="h1"
                        color="white"
                        sx={{
                            fontSize: "24px",
                            fontWeight: "700",
                            lineHeight: "31px",
                            marginBottom: "20px",
                            maxWidth: "70%",
                            margin: "30px auto 67px",
                            textAlign: "center",
                        }}
                    >
                        {pageData.page.failed.title}
                    </VuiTypography>
                    <VuiTypography
                        color="white"
                        sx={{
                            fontSize: "12px",
                            textAlign: "center",
                            lineHeight: "16px",
                            fontWeight: "700",
                            maxWidth: "320px",
                        }}
                    >
                        {pageData.page.failed.message}
                    </VuiTypography>
                    <VuiBox mt={4} mb={1}>
                        <VuiButton
                            type="button"
                            color="info"
                            onClick={() => setSuccess(true)}
                            sx={{
                                fontSize: "14px",
                                fontWeight: "700",
                                lineHeight: "21px",
                                padding: "12px 40px",
                            }}
                        >
                            <IoArrowBack size="14px" />
                            {pageData.page.failed.button}
                        </VuiButton>
                    </VuiBox>
                </VuiBox>
            )}
        </VuiBox>
    );
}
